import { DataCenterFinancialSnapshot } from '../../financial/DataCenterFinancialSnapshot';
/**
 * RiskAdjustedWaccSection (FIN-2) — discount-rate / hurdle stack (editorial).
 *
 * READ-ONLY over `report.financialAnalysis.headline`. Surfaces the persisted
 * WACC, the hurdle the verdict bands against — NAMED BY ITS PROVENANCE, via
 * `resolveHurdleProvenance`, because on most reports the hurdle is just the
 * WACC and calling it a "target IRR" claims a bar that was never set — and the
 * realised project IRR with its margin to that hurdle. W2a port:
 * the hand-rolled <dl> ledger → EditorialDatasheet (leader-dotted key/value
 * rows under a hairline group header, mono tabular values, toned value ink).
 * NEVER re-derives the discount rate — it formats persisted figures only.
 */
import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialDatasheet, EditorialCallout, type DatasheetRow } from '../../report/editorial';
import { resolveSectionCallout } from '../../../../lib/reportRendering/reportSectionCallouts';
import { fmtPct } from '../../../../lib/reportRendering/financialFormat';
import { resolveHurdleProvenance } from '../../../../config/reportPresentation';

function isNum(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export const RiskAdjustedWaccSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const h = report.financialAnalysis?.headline;
  const hasData = Boolean(h) && (isNum(h?.waccPct) || isNum(h?.hurdleRatePct) || isNum(h?.irrPct));

  const margin = h && isNum(h.irrPct) && isNum(h.hurdleRatePct) ? h.irrPct - h.hurdleRatePct : null;

  const callout = resolveSectionCallout(report, 'riskAdjustedWacc');
  // 3.4 / D15 — the hurdle carries its PROVENANCE. On most reports no target
  // return is set, so the "hurdle" simply IS the WACC — and for a conventional
  // cashflow NPV >= 0 <=> IRR >= WACC, which makes the IRR row a restatement of
  // the NPV test rather than a second bar that was cleared. Printing a bare
  // "Hurdle / target IRR" invited the reader to believe a target return had
  // been applied and met when nothing of the sort happened.
  const hurdle = resolveHurdleProvenance(report);

  const noteStyle: React.CSSProperties = { color: roleColor(theme, 'faint') };
  const keyWithNote = (keyLabel: string, note: string | undefined): React.ReactNode =>
    note ? (
      <>
        {keyLabel}
        <span className="block text-[10px]" style={noteStyle}>{note}</span>
      </>
    ) : keyLabel;

  const rows: DatasheetRow[] = h
    ? [
        { key: 'WACC (discount rate)', value: fmtPct(h.waccPct) },
        {
          key: keyWithNote(`Hurdle rate – ${hurdle.label}`, hurdle.note),
          value: fmtPct(h.hurdleRatePct),
        },
        {
          key: keyWithNote(
            'Project IRR',
            margin != null
              ? `${margin >= 0 ? '+' : '−'}${Math.abs(margin).toFixed(1)} pts vs ${hurdle.label}`
              : undefined,
          ),
          value: fmtPct(h.irrPct),
          tone: margin == null ? 'default' : margin >= 0 ? 'positive' : 'negative',
        },
      ]
    : [];

  if (report.financialAnalysis?.modelKind === 'data-center-capacity-fee') return <SectionShell label={label} hasData><DataCenterFinancialSnapshot stamp={report.financialAnalysis} section="riskAdjustedWacc" /></SectionShell>;

  return (
    <SectionShell label={label} hasData={hasData}>
      {h ? (
        // Carrier div holds the testid (EditorialDatasheet has no testid prop).
        <div data-testid="risk-adjusted-wacc-ledger">
          <EditorialDatasheet
            theme={theme}
            groups={[{ heading: 'Rates & returns', rows }]}
          />
          {callout && (
            <EditorialCallout theme={theme} tone={callout.tone} label={callout.label} style={{ marginTop: 8 }}>
              {callout.body}
            </EditorialCallout>
          )}
        </div>
      ) : null}
    </SectionShell>
  );
};
